import { CalendarIcon } from '@radix-ui/react-icons';
import { endOfDay, format, isBefore, startOfDay } from 'date-fns';
import { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ComponentProps, ReactNode, useId, useState } from 'react';
import { TrashIcon } from 'lucide-react';

type Props = {
	defaultValue?: DateRange;
	onSelect?: (range: DateRange | undefined, selectedDay: Date) => void;
	onClear?: () => void;
	isClearable?: boolean;
	className?: string;
	isDisabledFn?: ComponentProps<typeof Calendar>['disabled'];
	label?: string;
};

export default function DateRangePicker({
	className,
	defaultValue,
	onSelect,
	isDisabledFn,
	onClear,
	isClearable,
	label
}: Props) {
	const [range, setRange] = useState<DateRange | undefined>(defaultValue);

	const initRangeWithStartDay = (day: Date) => {
		const auxRange: DateRange = {
			from: startOfDay(day),
			to: undefined
		};
		setRange(auxRange);
		onSelect?.(auxRange, day);
	};

	const selectRange = (range: DateRange | undefined, selectedDay: Date) => {
		setRange(range);
		onSelect?.(range, selectedDay);
	};

	const onSelectInternal = (_: DateRange | undefined, selectedDay: Date) => {
		if (range?.to && range.from) {
			initRangeWithStartDay(selectedDay);
			return;
		}

		if (range?.from && !range.to) {
			if (isBefore(selectedDay, range.from)) {
				initRangeWithStartDay(selectedDay);
				return;
			}

			selectRange(
				{
					from: range.from,
					to: endOfDay(selectedDay)
				},
				selectedDay
			);

			return;
		}

		initRangeWithStartDay(selectedDay);
	};

	const onResetInternal = () => {
		setRange(undefined);
		onClear?.();
	};

	const renderDateRange = (): ReactNode => {
		if (!range?.from) {
			return <span>Pick a date</span>;
		}

		if (!range?.to) {
			return format(range.from, 'LLL dd, y');
		}

		return `${format(range.from, 'LLL dd, y')} – ${format(range.to, 'LLL dd, y')}`;
	};

	const triggerID = useId();
	return (
		<div className={className}>
			<Popover>
				{label && (
					<label className="text-sm leading-0" htmlFor={triggerID}>
						{label}
					</label>
				)}
				<PopoverTrigger asChild>
					<Button
						id={triggerID}
						variant={'outline'}
						className={cn('w-full justify-start text-left font-normal', !range && 'text-muted-foreground', {
							'mt-2': label
						})}
					>
						<CalendarIcon className="mr-2 h-4 w-4" />
						{renderDateRange()}

						{isClearable && range && (range.from || range.to) && (
							<div
								role="button"
								className="ml-auto hover:text-muted-foreground"
								onClick={e => {
									onResetInternal();
									e.stopPropagation();
								}}
							>
								<TrashIcon className="size-4" />
							</div>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						autoFocus
						mode="range"
						defaultMonth={range?.from}
						selected={range}
						onSelect={onSelectInternal}
						numberOfMonths={2}
						showOutsideDays={false}
						disabled={isDisabledFn}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
}
